<template>
  <form-wrapper full-width>
    <template v-slot:body>
      <div class="user__form">
        <div class="mt-2 col-12 col-md-8 col-xl-6 m-auto">
          <div class="default-table">
            <div class="px-1 pt-1 d-flex align-items-center">
              <h6 class="text-main">
                Add Permissions ({{ selectedItems.length }})
              </h6>
            </div>
            <div class="d-bloc d-md-flex">
              <div class="col-12 col-md-6 mr-1">
                <b-dropdown
                  variant="bg-white"
                  class="dropdown-icon-wrapper"
                >
                  <template #button-content>
                    <span v-if="gettingRolesError">Something went wrong</span>
                    <span v-else-if="rolesReady">{{ selectedRole }}</span>
                    <div
                      v-else
                      class="loading"
                    >
                      <GjIcon name="Loader" />
                    </div>
                    <GjIcon
                      class="icon-right"
                      name="ArrowDown"
                    />
                  </template>
                  <template v-for="role in roles">
                    <b-dropdown-item
                      :key="role.id"
                      @click="updateFilter(role.id)"
                    >
                      {{ role.name }}
                    </b-dropdown-item>
                  </template>
                </b-dropdown>
              </div>
              <b-input-group class="col-12 mt-1 mt-md-0 col-md-6">
                <b-input-group-prepend is-text>
                  <b-icon-search />
                </b-input-group-prepend>
                <b-form-input
                  id="filterInput"
                  v-model="filter"
                  type="search"
                  placeholder="Type to Search"
                />
              </b-input-group>
            </div>
            <div class="position-relative d-flex h-100">
              <b-table
                ref="table"
                :items="permissions"
                :fields="fields"
                :class="{ loaded: !loading }"
                show-empty
                responsive
                select-mode="multi"
                selectable
                no-select-on-click
                @row-clicked="onRowClicked"
              >
                <template #empty>
                  <div
                    v-if="loading"
                    class="loading-table text-center py-3"
                  >
                    <b-spinner variant="primary" />
                  </div>
                  <div
                    v-else
                    class="no-permissions"
                  >
                    <p class="px-1 pt-1">
                      No permissions found...
                    </p>
                  </div>
                </template>

                <template v-slot:head(selected)="">
                  <a
                    @click="selectAllRows()"
                  ><select-button
                    :selected="allSelected"
                  /></a>
                </template>
                <template #cell(selected)="{ item }">
                  <select-button :selected="isSelected(item.id)" />
                </template>
              </b-table>
              <table-busy v-if="loading && permissions.length > 0" />
            </div>
            <div
              v-if="permissionsCount > 0"
              class="default-pagination"
            >
              <b-pagination
                v-model="currentPage"
                :total-rows="totalCount"
                :per-page="pageSize"
                first-number
                last-number
              />
              <div class="results">
                Viewing {{ first.toLocaleString() }} to {{ last.toLocaleString() }} of {{ totalCount.toLocaleString() }} Items
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
    <template v-slot:footer>
      <b-button
        variant="bg-white"
        @click="closePermissions()"
      >
        Cancel
      </b-button>
      <b-button
        variant="bg-primary"
        class="ml-1"
        @click="addPermissions()"
      >Add Permissions</b-button>
    </template>
  </form-wrapper>
</template>
<script>
import { BIconSearch } from 'bootstrap-vue'

export default {
  name: 'Permissions',
  components: {
    BIconSearch,
  },
  data() {
    return {
      currentPage: 1,
      fields: [
        { key: 'selected', label: '', variant: 'select' },
        { key: 'title', label: 'Permission' },
      ],
      allRowsSelected: false,
      selectedItems: [],
      filter: null,
      filterOn: ['title'],
      roles: [],
      searchQuery: '',
      rolesReady: false,
      gettingRolesError: false,
      typingTimer: null,
      doneTypingInterval: 500,
    }
  },
  computed: {
    loading() {
      return this.$store.state.roles.fetchingPermissions
    },
    permissions() {
      return this.$store.state.roles.permissions
    },
    permissionsCount() {
      return this.permissions.length
    },
    selectedRole() {
      return this.roles.find(role => role.id === this.searchQuery).name
    },
    resultInfo() {
      return this.$store.state.roles.permissionsResultInfo
    },
    totalCount() {
      return this.resultInfo.totalCount
    },
    pageSize() {
      return this.resultInfo ? this.resultInfo.pageSize : 10
    },
    pageIndex() {
      return this.currentPage - 1
    },
    first() {
      return this.pageIndex * this.pageSize + 1
    },
    last() {
      return this.currentPage * this.pageSize < this.totalCount
        ? this.currentPage * this.pageSize
        : this.totalCount
    },
    allSelected() {
      return (
        this.permissions.length !== 0
        && this.permissions.every(i => this.selectedItems.find(s => s.id === i.id))
      )
    },
  },
  watch: {
    currentPage() {
      this.updatePermissions()
    },
    filter() {
      clearTimeout(this.typingTimer)
      this.typingTimer = setTimeout(() => {
        if (this.currentPage === 1) {
          this.updatePermissions()
        } else {
          this.currentPage = 1
        }
      }, this.doneTypingInterval)
    },
    fetchingPermissions() {
      return this.$store.state.roles.fetchingPermissions
    },
  },
  created() {
    this.updatePermissions()
    this.selectedItems = [...this.$store.getters['roles/selectedPermissions']]
    this.$store
      .dispatch('roles/fetchRoles', {
        pageIndex: 0,
        pageSize: 500,
      })
      .then(() => {
        this.roles = this.$store.state.roles.roles
        this.roles.unshift({
          id: '',
          name: 'All',
        })
        this.rolesReady = true
      })
      .catch(error => {
        this.rolesReady = true
        this.gettingRolesError = true
        this.errorToast(error.response?.data?.errors[0] || 'Something went wrong while fetching roles.')
      })
  },
  methods: {
    updatePermissions() {
      const query = {
        pageIndex: this.pageIndex,
        pageSize: this.pageSize,
        role: this.searchQuery,
        search: this.filter,
      }
      this.$store.dispatch('roles/fetchPermissions', query)
    },
    updateFilter(id) {
      this.searchQuery = id
      if (this.currentPage === 1) {
        this.updatePermissions()
      } else {
        this.currentPage = 1
      }
    },
    addPermissions() {
      this.$store.commit('roles/SET_SELECTED_PERMISSIONS', this.selectedItems)
      this.closePermissions()
    },
    onRowSelected(items) {
      this.selectedItems = items
    },
    selectAllRows() {
      if (this.allSelected) {
        for (let i = 0; i < this.permissions.length; i++) {
          this.selectedItems = this.selectedItems.filter(
            item => item.id !== this.permissions[i].id,
          )
        }
      } else {
        for (let i = 0; i < this.permissions.length; i++) {
          if (
            !this.selectedItems.some(item => item.id === this.permissions[i].id)
          ) {
            this.selectedItems.push(this.permissions[i])
          }
        }
      }
    },
    closePermissions() {
      this.$emit('close')
    },
    onRowClicked(item) {
      if (this.selectedItems.some(i => i.id === item.id)) {
        this.selectedItems = this.selectedItems.filter(i => i.id !== item.id)
        return
      }

      this.selectedItems.push(item)
    },
    isSelected(id) {
      return this.selectedItems.some(item => id === item.id)
    },
  },
}
</script>
<style lang="scss" scoped>
#filterInput {
  &:focus,
  :focus-within {
    border-color: #e0e5eb !important;
  }
}
.input-group:not(.bootstrap-touchspin):focus-within .input-group-text {
  border-color: #e0e5eb !important;
}
</style>
